import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, CalendarDays } from 'lucide-react';
import { format, startOfWeek, endOfWeek, isSameMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

export interface WeeklyCalendarHeaderProps {
  currentDate: Date;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  isLoading?: boolean;
}

export function WeeklyCalendarHeader({
  currentDate,
  onPrevWeek,
  onNextWeek,
  onToday,
  viewMode,
  onViewModeChange,
  isLoading = false,
}: WeeklyCalendarHeaderProps) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

  const formatPeriodLabel = () => {
    const startDay = format(weekStart, 'd');
    const endDay = format(weekEnd, 'd');
    const startYear = format(weekStart, 'yyyy');
    const endYear = format(weekEnd, 'yyyy');

    if (isSameMonth(weekStart, weekEnd)) {
      const monthYear = format(weekStart, "MMMM 'de' yyyy", { locale: ptBR });
      return `${startDay} a ${endDay} de ${monthYear.charAt(0).toUpperCase() + monthYear.slice(1)}`;
    }

    if (startYear === endYear) {
      const startMonth = format(weekStart, 'MMM', { locale: ptBR });
      const endMonthYear = format(weekEnd, "MMMM 'de' yyyy", { locale: ptBR });
      return `${startDay} de ${startMonth} a ${endDay} de ${endMonthYear}`;
    }

    const startLabel = format(weekStart, "d 'de' MMM 'de' yyyy", { locale: ptBR });
    const endLabel = format(weekEnd, "d 'de' MMM 'de' yyyy", { locale: ptBR });
    return `${startLabel} a ${endLabel}`;
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between py-2 border-b border-border/60">
      {/* Navegação da semana */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center rounded-lg border border-border bg-background p-0.5 shadow-sm">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onPrevWeek}
            disabled={isLoading}
            aria-label="Semana anterior"
            className="h-8 w-8 p-0 rounded-md hover:bg-muted"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToday}
            disabled={isLoading}
            className="h-8 px-2.5 text-xs font-medium hover:bg-muted"
          >
            Hoje
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onNextWeek}
            disabled={isLoading}
            aria-label="Próxima semana"
            className="h-8 w-8 p-0 rounded-md hover:bg-muted"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-foreground">
          <CalendarDays className="h-4 w-4 text-primary hidden sm:inline-block" />
          <span className="capitalize">{formatPeriodLabel()}</span>
        </div>
      </div>

      {/* Alternador de visualização: Grade Semanal vs Lista */}
      <div className="flex items-center rounded-lg border border-border bg-muted/40 p-1 shadow-sm self-start sm:self-auto">
        <button
          type="button"
          onClick={() => onViewModeChange('grid')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
            viewMode === 'grid'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 text-primary" />
          <span>Grade Semanal</span>
        </button>
        <button
          type="button"
          onClick={() => onViewModeChange('list')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
            viewMode === 'list'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <List className="h-3.5 w-3.5" />
          <span>Lista</span>
        </button>
      </div>
    </div>
  );
}
